import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Minus, Plus, Pencil, XCircle } from "lucide-react";
import { toast } from "sonner";

// Edit / cancel an open paint order (quantities + note). Edits are silent; cancelling texts Dave.
export default function PaintOrderActions({ order, onChanged }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/paint/config").then((r) => setCfg(r.data));
    setQty(Object.fromEntries(order.lines.map((l) => [l.key, l.qty])));
    setNote(order.note || "");
  }, [open, order]);

  const step = (k, d) => setQty((p) => ({ ...p, [k]: Math.max(0, (p[k] || 0) + d) }));
  const count = Object.values(qty).reduce((a, b) => a + (b || 0), 0);

  const save = async () => {
    if (!count) { toast.error("Add at least one item — or cancel the order instead"); return; }
    setBusy(true);
    try {
      await api.patch(`/paint/orders/${order.id}`, { items: qty, note });
      toast.success("Order updated");
      setOpen(false); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm(`Cancel the paint order for ${order.customer.name}? The Kilted Courier will be notified.`)) return;
    setBusy(true);
    try {
      await api.post(`/paint/orders/${order.id}/cancel`);
      toast.success("Order cancelled — we've let the courier know");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`paint-edit-${order.id}`} className="h-7 gap-1 text-xs"><Pencil className="h-3 w-3" /> Edit</Button>
        <Button size="sm" variant="ghost" onClick={cancel} disabled={busy} data-testid={`paint-cancel-${order.id}`} className="h-7 gap-1 text-xs text-destructive hover:bg-destructive/10"><XCircle className="h-3 w-3" /> Cancel</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="paint-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit order — {order.customer.name}</DialogTitle>
            <DialogDescription>Change quantities or the driver note. You can edit until the driver picks it up.</DialogDescription>
          </DialogHeader>
          {!cfg ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : (
            <div className="space-y-2">
              {cfg.items.map((it) => (
                <div key={it.key} className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm" data-testid={`paint-edit-row-${it.key}`}>
                  <span>{it.label} <span className="text-xs text-muted-foreground">${it.price.toFixed(2)}</span></span>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => step(it.key, -1)} data-testid={`paint-edit-minus-${it.key}`}><Minus className="h-3 w-3" /></Button>
                    <span className="w-6 text-center font-bold" data-testid={`paint-edit-qty-${it.key}`}>{qty[it.key] || 0}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => step(it.key, 1)} data-testid={`paint-edit-plus-${it.key}`}><Plus className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
              <Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2} placeholder="Delivery note for the driver (optional)" data-testid="paint-edit-note-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={save} disabled={busy || !cfg} data-testid="paint-edit-save" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
